import { useState } from "react";
import { useAcceptTerms } from "@/features/terms/hooks/useAcceptTerms";
import { BotMessage } from "../../components/BotMessage";
import { TurnMessages } from "../../components/TurnMessages";
import { QuickReplies } from "../../components/QuickReplies";
import { TypingIndicator } from "../../components/TypingIndicator";
import { ChatTurnLayout } from "../../components/ChatTurnLayout";
import { useTurnMessages } from "../../hooks/useTurnMessages";
import { chatCopy } from "../../engine/copy";
import { ChatStepId, type ChatTurnProps } from "../../types/chat";
import {
  anafConsentLines,
  renewalConsentLines,
  termsConsentLines,
} from "../consentLines";

const TermsReply = { Accept: "accept", Why: "why" } as const;

// BC and ANAF consent in one answer, the terms one tap away. In the
// preliminary chat both were asked before KYC: ANAF comes back here only when
// the lender could not record it then. A returning user whose offers expired
// gives both again for a new offer request.
export function TermsTurn({ step, isPromptTyped, onAnswered }: ChatTurnProps) {
  const isAnafOnly = step.id === ChatStepId.AnafConsent;
  const lines = isAnafOnly
    ? anafConsentLines
    : step.id === ChatStepId.OfferRenewal
      ? renewalConsentLines
      : termsConsentLines;
  const { messages, addUser, addBot, addError } = useTurnMessages();
  // Once explained, only the consent itself is left to give.
  const [isWhyAnswered, setIsWhyAnswered] = useState(false);
  // The bot types while the consent is saved, until the next step takes
  // over; the replies come back only when saving failed.
  const [isSaving, setIsSaving] = useState(false);
  const { acceptTerms } = useAcceptTerms({ onError: addError });

  const handleReply = async (reply: string) => {
    if (reply === TermsReply.Why) {
      addUser("De ce este necesar?");
      addBot(chatCopy.consentWhy);
      setIsWhyAnswered(true);
      return;
    }

    addUser("Da, sunt de acord");
    setIsSaving(true);
    if (
      await acceptTerms({ includesBc: !isAnafOnly, acceptsMarketing: false })
    ) {
      onAnswered();
      return;
    }
    setIsSaving(false);
  };

  return (
    <ChatTurnLayout
      messageCount={messages.length + Number(isSaving)}
      thread={
        <>
          <BotMessage lines={lines} isTyped={isPromptTyped} />
          <TurnMessages messages={messages} />
          {isSaving && <TypingIndicator />}
        </>
      }
      actions={
        !isSaving && (
          <QuickReplies
            onSelect={(reply) => void handleReply(reply)}
            replies={[
              { id: TermsReply.Accept, label: "Da, sunt de acord 👍" },
              ...(isWhyAnswered
                ? []
                : [{ id: TermsReply.Why, label: "De ce este necesar? 🤔" }]),
            ]}
          />
        )
      }
    />
  );
}
